/* eslint-disable sonarjs/cognitive-complexity */
// 9fbef606107a605d69c0edbcd8029e5d
import { NextSeo } from 'next-seo';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { getSiteId, isGymboree, getAPIConfig } from '@tcp/core/src/utils';
import SizeTable from '../molecules/SizeTable';
import UnitSwitch from '../molecules/UnitSwitch';
import FitSelector from '../molecules/FitSelector';
import CategoryContent from '../molecules/CategoryContent';
import CategorySelector from '../molecules/CategorySelector';
import DepartmentSelector from '../molecules/DepartmentSelector';
import {
  Container,
  Header,
  HeaderContent,
  TitleContainer,
  Title,
} from '../styles/DynamicSizeChart.style';

const tcpDepartmentIdMapping = {
  girls: 'girl',
  boys: 'boy',
  toddler: 'unisex',
  baby: 'unisex',
};

const gymDepartmentIdMapping = {
  'toddler-girl': 'girl',
  'toddler-boy': 'boy',
  'toddler-shoe': 'shoes',
};

const getKeyBasedOnLocale = (key) => {
  const { locale } = getAPIConfig();
  const lowerLocale = locale?.toLowerCase();
  if (lowerLocale === 'es_us') {
    return {
      NAME: 'NOMBRE',
      SIZE: 'TALLA',
    }[key];
  }
  if (lowerLocale === 'fr_ca') {
    return {
      NAME: 'NOM',
      SIZE: 'TAILLE',
    }[key];
  }

  return {
    NAME: 'NAME',
    SIZE: 'SIZE',
  }[key];
};

export const getActiveSizeChartValue = (key) => {
  const mapping = isGymboree() ? gymDepartmentIdMapping : tcpDepartmentIdMapping;
  return mapping[key] || null;
};

export const getActiveDepartment = (primaryBrand, departmentVal, activeSizeChartValue) => {
  if (!isEmpty(activeSizeChartValue)) {
    return activeSizeChartValue;
  }
  if (primaryBrand === 'SNJ') {
    return 'tween';
  }
  if (primaryBrand === 'PJP') {
    return 'adults';
  }
  return departmentVal.departments[0].departmentId;
};

export const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);

export const getMatchedDepartment = (activeSizeChart, data) => {
  if (!activeSizeChart || !data?.departments) return null;
  const activeChartKey = getActiveSizeChartValue(activeSizeChart);
  return data.departments.find(({ departmentId }) => departmentId === activeChartKey);
};

export const getSizeChartHeadingValue = (activeSizeChart, data) => {
  if (isEmpty(activeSizeChart)) return data.title;

  const matchedDepartment = getMatchedDepartment(activeSizeChart, data);

  if (isEmpty(matchedDepartment)) return data.title;

  const headingKey = `SEOHeading${capitalizeFirstLetter(activeSizeChart)}`;
  const seoHeading = matchedDepartment[headingKey] || matchedDepartment.SEOHeading;

  return !isEmpty(seoHeading) ? seoHeading : data.title;
};

export const getSizeCharSeoDescriptionValue = (activeSizeChart, data, isCanadaSite) => {
  const defaultValue = isCanadaSite ? data.defaultSEODescriptionCA : data.defaultSEODescription;
  if (isEmpty(activeSizeChart)) return defaultValue;

  const matchedDepartment = getMatchedDepartment(activeSizeChart, data);

  if (isEmpty(matchedDepartment)) return defaultValue;

  const matchedSEODescription = isCanadaSite
    ? matchedDepartment.SEODescriptionCA
    : matchedDepartment.SEODescription;
  const headingKey = isCanadaSite
    ? `SEODescription${capitalizeFirstLetter(activeSizeChart)}CA`
    : `SEODescription${capitalizeFirstLetter(activeSizeChart)}`;
  const seoHeading = matchedDepartment[headingKey] || matchedSEODescription;

  return !isEmpty(seoHeading) ? seoHeading : defaultValue;
};

export const getSEOProperty = (idVal, keyVal, type) => {
  const seoKey = idVal === 'unisex' ? keyVal : idVal;
  return `${type}${capitalizeFirstLetter(seoKey)}`;
};

export const getSEOValue = (keyVal, idVal, data, type) => {
  const matchedDepartment = getMatchedDepartment(keyVal, data);
  if (!matchedDepartment) return data[type];

  return matchedDepartment[getSEOProperty(idVal, keyVal, type)] || data[type];
};

export const getSEOTitle = (keyVal, idVal, data) => getSEOValue(keyVal, idVal, data, 'SEOTitle');

export const getSEOMetaDescription = (keyVal, idVal, data) =>
  getSEOValue(keyVal, idVal, data, 'SEOMetaDescription');

const DynamicSizeChart = ({
  departmentVal,
  categoryData,
  fetchCategories,
  isQuickView,
  primaryBrand,
  isModalView,
  activeSizeChart,
  isContentPage,
}) => {
  const siteId = getSiteId();
  const isCanadaSite = siteId === 'ca';
  const { hideUnitMetic = [] } = departmentVal;
  const activeSizeChartValue = getActiveSizeChartValue(activeSizeChart);
  const activeDepartmentValue = getActiveDepartment(
    primaryBrand,
    departmentVal,
    activeSizeChartValue
  );
  const [activeDepartment, setActiveDepartment] = useState(activeDepartmentValue);
  const activeCategoryVal = !isEmpty(categoryData) ? categoryData[activeDepartment] : null;
  const [activeCategory, setActiveCategory] = useState(activeCategoryVal?.name || null);
  const [activeFit, setActiveFit] = useState(activeCategoryVal?.category[0]?.fit[0]?.name || null);
  const [selectedUnit, setSelectedUnit] = useState(isCanadaSite ? 'METRIC' : 'US');
  const [isSizeTableLoaded, setIsSizeTableLoaded] = useState(false);
  const headingValue = getSizeChartHeadingValue(activeSizeChart, departmentVal);
  const pageTitle = getSEOTitle(activeSizeChart, activeSizeChartValue, departmentVal);
  const metaDescription = getSEOMetaDescription(
    activeSizeChart,
    activeSizeChartValue,
    departmentVal
  );
  const seoDescriptionValue = getSizeCharSeoDescriptionValue(
    activeSizeChart,
    departmentVal,
    isCanadaSite
  );

  useEffect(() => {
    if (activeDepartment !== activeDepartmentValue) {
      setActiveDepartment(activeDepartmentValue);
    }
  }, [activeDepartmentValue]);

  useEffect(() => {
    if (activeDepartment && !activeCategoryVal) {
      fetchCategories(activeDepartment);
    }
  }, [activeDepartment, activeCategoryVal]);

  useEffect(() => {
    if (activeCategoryVal) {
      setActiveCategory(activeCategoryVal.category[0]?.name || null);
    }
  }, [activeCategoryVal]);

  useEffect(() => {
    if (activeCategoryVal && activeCategory) {
      const category = activeCategoryVal.category.find((cat) => cat.name === activeCategory);
      setActiveFit(category?.fit[0]?.name || null);
    }
  }, [activeCategory, activeCategoryVal]);

  const handleDepartmentClick = useCallback(
    (departmentId) => {
      if (departmentId !== activeDepartment) {
        setActiveDepartment(departmentId);
        setActiveCategory(null);
        setActiveFit(null);
        setIsSizeTableLoaded(false);
      }
    },
    [activeDepartment]
  );

  const handleCategoryTabChange = useCallback(
    (categoryName) => {
      if (categoryName !== activeCategory) {
        setActiveCategory(categoryName);
        setActiveFit(null);
      }
    },
    [activeCategory]
  );

  const handleFitTabChange = useCallback(
    (fitName) => {
      if (fitName !== activeFit) {
        setActiveFit(fitName);
      }
    },
    [activeFit]
  );

  const handleUnitChange = useCallback((unit) => {
    setSelectedUnit(unit);
  }, []);

  const getFilteredValue = useCallback(
    (key, value) => {
      if (key !== getKeyBasedOnLocale('SIZE') && typeof value === 'string') {
        const parts = value.split('|');

        if (selectedUnit === 'US') {
          return parts[0] ? parts[0].trim() : parts;
        }
        if (selectedUnit === 'METRIC') {
          return parts[1] ? parts[1].trim() : parts;
        }
      }
      return value;
    },
    [selectedUnit]
  );

  const tableData = useMemo(() => {
    if (isEmpty(activeCategoryVal)) return null;
    const activeCategoryData = activeCategoryVal.category.find(
      (cat) => cat.name === activeCategory
    );
    if (isEmpty(activeCategoryData)) return null;

    const { fit, size } = activeCategoryData;
    const activeFitData = fit.find((item) => item.name === activeFit);

    const filterData = (data) => {
      const { hideColumnName = [] } = data;
      if (hideColumnName.length === 0) return data;
      const clonedData = cloneDeep(data);
      return {
        ...clonedData,
        size: clonedData.size.map((item) => {
          const filteredItem = { ...item };
          hideColumnName.forEach((key) => {
            delete filteredItem[key];
          });
          return filteredItem;
        }),
      };
    };

    if (isEmpty(size) && !isEmpty(fit) && !isEmpty(activeFitData)) {
      const filteredFitData = filterData(activeFitData);
      return activeDepartment && activeCategoryData && filteredFitData;
    }
    const filteredCategoryData = filterData(activeCategoryData);
    return activeDepartment && !isEmpty(size) && filteredCategoryData;
  }, [categoryData, activeCategory, activeFit]);

  useEffect(() => {
    if (tableData) {
      setIsSizeTableLoaded(true);
    }
  }, [tableData]);

  return (
    <>
      <NextSeo title={pageTitle} description={metaDescription} />
      <Container className="dynamic-size-chart-container" isModalView={isModalView}>
        <Header>
          <HeaderContent className="dynamic-header-section">
            <TitleContainer className="dynamic-title-content-section" tabIndex={0}>
              <Title>{headingValue}</Title>
            </TitleContainer>
            <UnitSwitch
              selectedUnit={selectedUnit}
              onUnitChange={handleUnitChange}
              activeDepartment={activeDepartment}
              hideUnitMetic={hideUnitMetic}
            />
          </HeaderContent>
        </Header>
        {isEmpty(activeSizeChart) && (
          <DepartmentSelector
            departmentData={departmentVal}
            activeDepartment={activeDepartment}
            onSelectDepartment={handleDepartmentClick}
            isQuickView={isQuickView}
          />
        )}
        {activeCategoryVal && (
          <CategorySelector
            categoryData={activeCategoryVal}
            activeCategory={activeCategory}
            onSelectCategory={handleCategoryTabChange}
          />
        )}
        {activeCategoryVal && activeCategory && activeFit && (
          <FitSelector
            categoryData={activeCategoryVal}
            activeCategory={activeCategory}
            activeFit={activeFit}
            onSelectFit={handleFitTabChange}
          />
        )}
        {activeCategory && tableData ? (
          <SizeTable
            sizeData={tableData}
            selectedUnit={selectedUnit}
            getFilteredValue={getFilteredValue}
            onLoad={() => setIsSizeTableLoaded(true)}
          />
        ) : (
          <></>
        )}
        <CategoryContent
          tableData={tableData}
          isQuickView={isQuickView}
          categoryData={activeCategoryVal}
          isSizeTableLoaded={isSizeTableLoaded}
          isContentPage={isContentPage}
          seoDescriptionValue={seoDescriptionValue}
        />
      </Container>
    </>
  );
};

export default DynamicSizeChart;
